/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import TabView from '@ion/dls/src/components/tabs/TabView';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {Headertabs} from './types';

/**
 * Props for the HeaderBar component.
 */
interface HeaderBarProps {
  value: Headertabs;
  onChange: (event: React.ChangeEvent<{}>, newValue: Headertabs) => void;
  showScheduledReports?: boolean;
  isSchedulerFleetManager?: boolean;
}

const useStyles = makeStyles((theme) => ({
  header : toggleThemeStyle({
    display         : 'flex',
    backgroundColor : dlsColor('white'),
    boxShadow       : '1px 2px 5px 0 rgba(0,0,0,0.1)'
  }, {
    display : 'flex'
  }),
  tab : {
    width                          : 'fit-content',
    marginLeft                     : '24px',
    borderBottom                   : toggleThemeStyle('unset', `2px solid ${dlsColor('pastelTeal')}`),
    [theme.breakpoints.down('sm')] : {
      marginLeft : 0
    }
  }
}));

/**
 * Style overrides for the TabView component used in the header.
 */
const tabStyles = () => ({
  backgroundStyle : toggleThemeStyle({
    color           : dlsColor('grey3'),
    fontSize        : '12px',
    backgroundColor : dlsColor('white')
  }, {
    fontSize      : '14px',
    fontFamily    : 'Roboto',
    letterSpacing : '0.5px',
    textTransform : 'capitalize'
  }),
  selected : toggleThemeStyle({
    color : `${dlsColor('caBlue')} !important`
  }, {
    color : `${dlsColor('darkTeal')} !important`
  }),
  customTabIndicator : toggleThemeStyle({
    backgroundColor : `${dlsColor('caBlue')} !important`
  }, {
    backgroundColor : `${dlsColor('darkTeal')} !important`
  })
});

/**
 * HeaderBar component renders a tab view for dashboard filtering.
 * 
 * Used to switch between 'All' and 'Recent' dashboards.
 *
 * ```typescript
 * <HeaderBar value={Headertabs.All} 
 *   onChange={(e, newValue) => console.log(newValue)} />
 * ```
 */
export const HeaderBar = ({
  value,
  onChange,
  showScheduledReports = false,
  isSchedulerFleetManager = false
}: HeaderBarProps) => {
  const classes = useStyles();

  const tabs = useMemo(() => {
    const items = [
      {tabHeader: translate('sisense:analytics.topNavigation.tabs.all'), tab: Headertabs.All},
      {tabHeader: translate('sisense:analytics.topNavigation.tabs.recent'), tab: Headertabs.Recent}
    ];

    if (showScheduledReports) {
      items.push({
        tabHeader : translate('sisense:analytics.topNavigation.tabs.scheduledReports'),
        tab       : Headertabs.ScheduledReports
      });
      if (isSchedulerFleetManager) {
        items.push({
          tabHeader : translate('sisense:analytics.topNavigation.tabs.migrationResults'),
          tab       : Headertabs.MigrationResults
        });
      }
    }

    return items;
  }, [showScheduledReports]);

  const selectedTab = (
    !showScheduledReports && value === Headertabs.ScheduledReports ? Headertabs.All : value
  );

  return (
    <div className={classes.header}>
      <div className={classes.tab}>
        <TabView tabItems={tabs}
          onChange={onChange}
          variant='standard'
          value={selectedTab}
          overrides={tabStyles}
          passSelected/>
      </div>
    </div>
  );
};

export default HeaderBar;
